/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './NotificationPreferences.css';

const CATEGORIES = {
  'Application / Project': [
    { key: 'application_submitted', label: 'Application Submitted' },
    { key: 'application_approved', label: 'Application Approved' },
    { key: 'application_rejected', label: 'Application Rejected' },
    { key: 'revision_requested', label: 'Revision Requested' },
    { key: 'application_revised', label: 'Application Revised' },
    { key: 'project_started', label: 'Project Started' },
    { key: 'project_completed', label: 'Project Completed' },
    { key: 'project_terminated', label: 'Project Terminated' },
  ],
  'Assignment': [
    { key: 'chamber_assigned', label: 'Compartment Assigned' },
    { key: 'chamber_updated', label: 'Compartment Updated' },
    { key: 'chamber_completed', label: 'Compartment Completed' },
    { key: 'chamber_deleted', label: 'Compartment Removed' },
    { key: 'resource_assigned', label: 'Resource Assigned' },
    { key: 'resource_updated', label: 'Resource Updated' },
  ],
  'Invoice': [
    { key: 'invoice_created', label: 'Invoice Created' },
    { key: 'invoice_edited', label: 'Invoice Edited' },
    { key: 'invoice_deleted', label: 'Invoice Deleted' },
    { key: 'invoice_status_changed', label: 'Invoice Status Changed' },
    { key: 'invoice_billed', label: 'Invoice Billed (Finalized & Sent)' },
    { key: 'invoice_payer_added', label: 'Added as an Invoice Payer' },
    { key: 'invoice_signed', label: 'Invoice Signed by All Payers' },
    { key: 'invoice_disputed', label: 'Invoice Disputed' },
    { key: 'invoice_dispute_resolved', label: 'Invoice Re-sent After a Dispute' },
    { key: 'payment_recorded', label: 'Payment Recorded' },
    { key: 'payment_edited', label: 'Payment Edited' },
    { key: 'payment_deleted', label: 'Payment Deleted' },
  ],
  'Request': [
    { key: 'request_submitted', label: 'Request Submitted' },
    { key: 'request_completed', label: 'Request Completed' },
    { key: 'request_rejected', label: 'Request Rejected' },
    { key: 'request_status_changed', label: 'Request Moved to New / Pending' },
    { key: 'request_admin_comment', label: 'Admin Commented on Request' },
    { key: 'request_client_comment', label: 'Reply on Request Thread' },
    { key: 'request_edited', label: 'Request Edited (by you)' },
    { key: 'request_admin_edited', label: 'Request Edited by Admin' },
    { key: 'request_deleted', label: 'Request Deleted' },
  ],
  'Team': [
    { key: 'team_member_added', label: 'Team Member Added' },
    { key: 'team_member_removed', label: 'Team Member Removed' },
    { key: 'team_role_changed', label: 'Team Role Changed' },
    { key: 'invitation_accepted', label: 'Invitation Accepted' },
    { key: 'invitation_declined', label: 'Invitation Declined' },
  ],
  'Renewal': [
    { key: 'project_renewal_confirmation', label: 'Renewal Confirmation' },
    { key: 'project_renewed', label: 'Project Renewed' },
  ],
  'General': [
    { key: 'general_comment', label: 'General Comment' },
  ],
  'Facility Chats': [
    { key: 'facility_chat_message', label: 'New Facility Chat Message', inAppOnly: true },
  ],
};

const KNOWN_KEYS = new Set(
  Object.values(CATEGORIES).flatMap(events => events.map(event => event.key))
);

const humanizeKey = (key) =>
  key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

const NotificationPreferences = ({ projectId = null, facilityId = null, onClose, hideHeader = false }) => {
  const [preferences, setPreferences] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const hasLoadedOnce = React.useRef(false);

  const isProjectSpecific = !!projectId;
  const isFacilitySpecific = !!facilityId;

  // Anything the server sends that this file doesn't list is surfaced under "Other"
  // so a newly added event type can never end up with no way to switch it off.
  const categories = useMemo(() => {
    const unmapped = Object.keys(preferences)
      .filter(key => {
        if (KNOWN_KEYS.has(key)) return false;
        const toggle = preferences[key];
        return !!toggle && typeof toggle === 'object' && ('in_app' in toggle || 'email' in toggle);
      })
      .sort()
      .map(key => ({ key, label: humanizeKey(key) }));

    return unmapped.length > 0 ? { ...CATEGORIES, Other: unmapped } : CATEGORIES;
  }, [preferences]);

  const allEvents = useMemo(() => Object.values(categories).flat(), [categories]);
  const allEmailEvents = useMemo(() => allEvents.filter(event => !event.inAppOnly), [allEvents]);

  let endpoint;
  if (projectId) {
    endpoint = `/api/notifications/preferences/${projectId}`;
  } else if (facilityId) {
    endpoint = `/api/notifications/preferences/facility/${facilityId}`;
  } else {
    endpoint = '/api/notifications/preferences';
  }

  useEffect(() => {
    const fetchPreferences = async () => {
      if (hasLoadedOnce.current) {
        setSwitching(true);
      } else {
        setInitialLoading(true);
      }
      try {
        const res = await axios.get(endpoint);
        setPreferences(res.data.preferences);
        hasLoadedOnce.current = true;
      } catch (err) {
        console.error('Error fetching preferences:', err);
        toast.error('Failed to load notification preferences');
      } finally {
        setInitialLoading(false);
        setSwitching(false);
      }
    };
    fetchPreferences();
  }, [endpoint]);

  useEffect(() => {
    setDirty(false);
  }, [endpoint]);

  const togglePref = useCallback((eventKey, channel) => {
    setPreferences(prev => ({
      ...prev,
      [eventKey]: {
        ...prev[eventKey],
        [channel]: !prev[eventKey]?.[channel]
      }
    }));
    setDirty(true);
  }, []);

  const toggleCategoryAll = useCallback((events, channel) => {
    const allEnabled = events.every(e => preferences[e.key]?.[channel]);
    setPreferences(prev => {
      const updated = { ...prev };
      events.forEach(e => {
        updated[e.key] = {
          ...updated[e.key],
          [channel]: !allEnabled
        };
      });
      return updated;
    });
    setDirty(true);
  }, [preferences]);

  const toggleExpanded = (categoryName) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryName)) next.delete(categoryName);
      else next.add(categoryName);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(endpoint, { preferences });
      toast.success('Notification preferences saved');
      setDirty(false);
    } catch (err) {
      console.error('Error saving preferences:', err);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const getCategorySummary = (events) => {
    const inAppCount = events.filter(e => preferences[e.key]?.in_app).length;
    const emailEvents = events.filter(e => !e.inAppOnly);
    const emailCount = emailEvents.filter(e => preferences[e.key]?.email).length;
    return { inAppCount, emailCount, total: events.length, emailTotal: emailEvents.length };
  };

  const categoryHasEmail = (events) => events.some(e => !e.inAppOnly);

  if (initialLoading) {
    return <div className="notif-prefs-loading">Loading preferences...</div>;
  }

  return (
    <div className="notif-prefs-container" style={{
      opacity: switching ? 0.5 : 1,
      pointerEvents: switching ? 'none' : 'auto',
      transition: 'opacity 0.15s ease'
    }}>
      {!hideHeader && (
        <div className="notif-prefs-header">
          <div className="notif-prefs-header-top">
            <h3>
              {isProjectSpecific
                ? 'Project Notification Preferences'
                : isFacilitySpecific
                  ? 'Facility Notification Preferences'
                  : 'Notification Preferences'}
            </h3>
            {onClose && (
              <button className="notif-prefs-close-btn" onClick={onClose} title="Close">&times;</button>
            )}
          </div>
          <p className="notif-prefs-subtitle">
            {isProjectSpecific
              ? 'These settings override your global defaults for this project only.'
              : isFacilitySpecific
                ? 'These settings apply to notifications from this facility.'
                : 'Choose which notifications you receive via in-app alerts and email.'}
          </p>
          <p className="notif-prefs-hint">Expand each section to customize individual activity types.</p>
        </div>
      )}

      <div className="notif-prefs-body">
        <div className="notif-prefs-master">
          <div className="notif-prefs-master-left">
            <span className="notif-prefs-master-name">All Notifications</span>
            <span className="notif-prefs-master-summary">
              Applies to every activity type below
            </span>
          </div>
          <div className="notif-prefs-category-toggles">
            <label className="notif-prefs-toggle-label">
              <input
                type="checkbox"
                checked={allEvents.length > 0 && allEvents.every(e => preferences[e.key]?.in_app)}
                onChange={() => toggleCategoryAll(allEvents, 'in_app')}
              />
              <span>In-App</span>
            </label>
            <label className="notif-prefs-toggle-label">
              <input
                type="checkbox"
                checked={allEmailEvents.length > 0 && allEmailEvents.every(e => preferences[e.key]?.email)}
                onChange={() => toggleCategoryAll(allEmailEvents, 'email')}
              />
              <span>Email</span>
            </label>
          </div>
        </div>

        {Object.entries(categories).map(([categoryName, events]) => {
          const allInApp = events.every(e => preferences[e.key]?.in_app);
          const emailEvents = events.filter(e => !e.inAppOnly);
          const allEmail = emailEvents.length > 0 && emailEvents.every(e => preferences[e.key]?.email);
          const hasEmail = categoryHasEmail(events);
          const isExpanded = expandedCategories.has(categoryName);
          const summary = getCategorySummary(events);

          return (
            <div key={categoryName} className={`notif-prefs-category ${isExpanded ? 'expanded' : ''}`}>
              <div className="notif-prefs-category-header" onClick={() => toggleExpanded(categoryName)}>
                <div className="notif-prefs-category-left">
                  <span className={`notif-prefs-chevron ${isExpanded ? 'open' : ''}`}>&#9656;</span>
                  <span className="notif-prefs-category-name">{categoryName}</span>
                  <span className="notif-prefs-category-summary">
                    {summary.inAppCount}/{summary.total} in-app
                    {hasEmail && <> &middot; {summary.emailCount}/{summary.emailTotal} email</>}
                  </span>
                </div>
                <div className="notif-prefs-category-toggles" onClick={(e) => e.stopPropagation()}>
                  <label className="notif-prefs-toggle-label">
                    <input
                      type="checkbox"
                      checked={allInApp}
                      onChange={() => toggleCategoryAll(events, 'in_app')}
                    />
                    <span>In-App</span>
                  </label>
                  {hasEmail && (
                    <label className="notif-prefs-toggle-label">
                      <input
                        type="checkbox"
                        checked={allEmail}
                        onChange={() => toggleCategoryAll(emailEvents, 'email')}
                      />
                      <span>Email</span>
                    </label>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="notif-prefs-events">
                  {events.map(event => (
                    <div key={event.key} className="notif-prefs-event-row">
                      <span className="notif-prefs-event-label">{event.label}</span>
                      <div className="notif-prefs-event-toggles">
                        <label className="notif-prefs-switch">
                          <input
                            type="checkbox"
                            checked={preferences[event.key]?.in_app ?? true}
                            onChange={() => togglePref(event.key, 'in_app')}
                          />
                          <span className="notif-prefs-slider" />
                          <span className="notif-prefs-channel-label">In-App</span>
                        </label>
                        {!event.inAppOnly && (
                          <label className="notif-prefs-switch">
                            <input
                              type="checkbox"
                              checked={preferences[event.key]?.email ?? false}
                              onChange={() => togglePref(event.key, 'email')}
                            />
                            <span className="notif-prefs-slider" />
                            <span className="notif-prefs-channel-label">Email</span>
                          </label>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="notif-prefs-footer">
        {onClose && !hideHeader && (
          <button className="notif-prefs-btn notif-prefs-btn-cancel" onClick={onClose}>
            Cancel
          </button>
        )}
        <button
          className="notif-prefs-btn notif-prefs-btn-save"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default NotificationPreferences;
